import { useState, useEffect, useCallback } from "react";

export interface SessionUser {
  id: string;
  name: string;
  email: string;
  role: string;
  garages: any[];
  staff: any | null;
  hasGarage: boolean;
  hasReferral: boolean;
  referralCode: string | null;
}

export interface AuthContextType {
  user: SessionUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  logout: () => Promise<void>;
}

export function useAuth() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const restoreSession = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch("/api/auth/session", {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        // Restore localStorage for compatibility with existing code
        localStorage.setItem("user", JSON.stringify(data.user));
        if (data.garage) {
          localStorage.setItem("garage-data", JSON.stringify(data.garage));
        }
      } else {
        setUser(null);
        localStorage.removeItem("user");
        localStorage.removeItem("garage-data");
      }
    } catch (error) {
      console.error("[useAuth] Failed to restore session:", error);
      setUser(null);
      localStorage.removeItem("user");
      localStorage.removeItem("garage-data");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("[useAuth] Logout error:", error);
    } finally {
      setUser(null);
      localStorage.removeItem("user");
      localStorage.removeItem("garage-data");
    }
  }, []);

  useEffect(() => {
    restoreSession();
  }, [restoreSession]);

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    logout,
    restoreSession,
  };
}
